/** The games a shop may tag itself with on its storefront (TRO-1157).
 *
 *  A tag says what a shop sells and nothing more: it is **signal only**, never a query. Tapping one
 *  does not narrow a list — the browse Filter control owns that — so a tag must not wear the pill
 *  shape the Location chips use, or the page offers two identical-looking chips where only one is
 *  tappable.
 *
 *  This is deliberately not `catalog/games.ts`'s `GAMES`, which admits a game only once its cards
 *  are recognizable by the scanner. A vendor sells what they sell whether or not Trovie can
 *  photograph it, and `pokemon-jp` scopes a scan rather than describing a shop. Where a value does
 *  match a `GAME_KEY` it is spelled the same; `dragonball` is one tag over two of them, and
 *  `yugioh`, `digimon`, `fleshandblood` and `starwars` have no catalog coverage at all.
 *
 *  The vocabulary is shared with the backend through `shared/fixtures/shop-game-tags.json`; see
 *  `shopGameTags.test.ts`, which holds this list and `trovie.domain.shop_tags` to that table. */
export type ShopGameTag = { value: string; label: string; };

export const SHOP_GAME_TAGS: readonly ShopGameTag[] = [
  { value: "pokemon", label: "Pokémon" },
  { value: "magic", label: "Magic: The Gathering" },
  { value: "lorcana", label: "Lorcana" },
  { value: "onepiece", label: "One Piece" },
  { value: "yugioh", label: "Yu-Gi-Oh!" },
  { value: "riftbound", label: "Riftbound" },
  { value: "dragonball", label: "Dragon Ball Super" },
  { value: "digimon", label: "Digimon" },
  { value: "fleshandblood", label: "Flesh and Blood" },
  { value: "starwars", label: "Star Wars: Unlimited" },
];

/** A shop that sells everything reads as a shop that sells nothing in particular, and the front
 *  door has room for two rows of chips. Mirrors `MAX_SHOP_GAME_TAGS` on the backend, which rejects
 *  a longer list. */
export const MAX_SHOP_GAME_TAGS = 8;

/** Returns the chip text for a stored tag value, falling back to the value itself so a tag written
 *  by a newer client still reads as something rather than disappearing. */
export const shopGameTagLabel = (value: string): string =>
  SHOP_GAME_TAGS.find((tag) => tag.value === value)?.label ?? value;
